import { Plate } from '../components/Card';
import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';

const api = {
  get:  (path, cfg) => axios.get(`${API}/api${path}`, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }),
  post: (path, data) => axios.post(`${API}/api${path}`, data, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }),
};

const downloadFile = async (path, filename, setLoading) => {
  try {
    if (setLoading) setLoading(true);
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API}/api${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      throw new Error(`Ошибка ${res.status}${errText ? ': ' + errText.slice(0, 100) : ''}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (e) {
    alert(`Не удалось скачать файл:\n${e.message}`);
  } finally {
    if (setLoading) setLoading(false);
  }
};

// ─── Кнопка скачивания Excel ─────────────────────────────────────────────────
function ExcelBtn({ orderId, docNumber }) {
  const [loading, setLoading] = useState(false);
  return (
    <button
      onClick={() => downloadFile(
        `/procurement/orders/${orderId}/export/xlsx`,
        `${docNumber}.xlsx`,
        setLoading,
      )}
      disabled={loading}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        color: '#fff', fontSize: 12,
        cursor: loading ? 'wait' : 'pointer',
        padding: '4px 12px', border: 'none',
        background: loading
          ? 'var(--text2)'
          : 'var(--accent)',
        borderRadius: 6, fontWeight: 600,
        boxShadow: '0 1px 4px rgba(30,36,37,0.075)',
        transition: 'background 0.2s',
        minWidth: 80,
      }}
    >
      {loading
        ? <><span style={{ fontSize: 13 }}>⏳</span> Загрузка...</>
        : <><span style={{ fontSize: 14 }}>📊</span> Excel</>
      }
    </button>
  );
}

// ─── Форматирование ──────────────────────────────────────────────────────────
const fmtMoney = (n) => {
  if (!n && n !== 0) return '—';
  n = Number(n);
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} млн ₽`;
  if (Math.abs(n) >= 1_000)     return `${(n / 1_000).toFixed(0)} тыс ₽`;
  return `${n.toFixed(0)} ₽`;
};
const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('ru-RU', { maximumFractionDigits: 1 }));

// ─── Цвета статусов ──────────────────────────────────────────────────────────
const URGENCY_COLOR = {
  critical: '#e04848',
  urgent:   '#e08a00',
  soon:     '#e08a00',
  ok:       '#0e9f6e',
};
const URGENCY_LABEL = {
  critical: 'Критично',
  urgent:   'Срочно',
  soon:     'Скоро',
  ok:       'ОК',
};
const TREND_ICON  = { growing: '📈', falling: '📉', stable: '➡️' };
const TREND_COLOR = { growing: '#0e9f6e', falling: '#e04848', stable: 'var(--text2)' };
const DELTA_COLOR = { understock: '#e04848', overstock: '#e08a00', ok: '#0e9f6e' };
const DELTA_LABEL = { understock: '↘ Недозакуп', overstock: '↗ Перезакуп', ok: '✓ Норма' };

// ─── Общие компоненты ─────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, color, icon }) {
  return (
    <div style={{
      background: 'var(--bg3)',
      borderRadius: 14, padding: '14px 16px 16px', minWidth: 0,
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6, minHeight: 30 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || 'var(--text1)', whiteSpace: 'nowrap', letterSpacing: '-0.02em' }}>
        {value ?? '—'}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function Badge({ type, label }) {
  const color = URGENCY_COLOR[type] || '#888';
  return (
    <span style={{
      background: `${color}22`, color, border: `1px solid ${color}44`,
      borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700,
      letterSpacing: '0.04em',
    }}>{label || URGENCY_LABEL[type] || type}</span>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        border: '3px solid var(--border)', borderTopColor: 'var(--accent)',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function SectionHeader({ title, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 14, marginTop: 24 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: 'var(--text1)' }}>{title}</h2>
      {action}
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 12, overflow: 'hidden', ...style,
    }}>{children}</div>
  );
}

// ─── Таблица позиций ─────────────────────────────────────────────────────────
function ItemsTable({ items, loading, focusSku }) {
  const [search, setSearch] = useState('');
  const [filterUrgency, setFilterUrgency] = useState('all');
  const [filterCat, setFilterCat] = useState('all');
  const [sortKey, setSortKey] = useState('score');
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const categories = [...new Set(items.map(i => i.category))].sort();

  const filtered = items
    .filter(i => filterUrgency === 'all' || i.urgency === filterUrgency)
    .filter(i => filterCat === 'all' || i.category === filterCat)
    .filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase())
                         || i.sku.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const va = a[sortKey] ?? 0, vb = b[sortKey] ?? 0;
      return sortAsc ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });

  const pages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Диплинк из Telegram: /procurement?sku=ART — фильтр и прокрутка к строке
  useEffect(() => {
    const q = (focusSku || '').trim();
    if (!q || loading || items.length === 0) return;
    setSearch(q);
    setPage(0);
    const t = window.setTimeout(() => {
      try {
        const el = document.querySelector(`[data-proc-sku="${CSS.escape(q)}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.style.transition = 'outline 0.2s';
          el.style.outline = '2px solid var(--accent)';
          window.setTimeout(() => { el.style.outline = ''; }, 2600);
        }
      } catch (_) { /* старые браузеры без CSS.escape */ }
    }, 200);
    return () => window.clearTimeout(t);
  }, [focusSku, loading, items.length]);

  const Th = ({ k, label }) => (
    <th
      onClick={() => { setSortAsc(sortKey === k ? !sortAsc : false); setSortKey(k); }}
      style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text2)', fontSize: 12,
        cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none',
        color: sortKey === k ? 'var(--accent)' : 'var(--text2)' }}
    >
      {label} {sortKey === k ? (sortAsc ? '↑' : '↓') : ''}
    </th>
  );

  return (
    <div>
      {/* Фильтры */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          placeholder="🔍 Поиск по SKU / названию..."
          style={{ flex: 1, minWidth: 200, padding: '8px 12px', background: 'var(--bg3)',
            border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text1)', fontSize: 13 }}
        />
        <select value={filterUrgency} onChange={e => { setFilterUrgency(e.target.value); setPage(0); }}
          style={{ padding: '8px 12px', background: 'var(--bg3)', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--text1)', fontSize: 13 }}>
          <option value="all">Все статусы</option>
          <option value="critical">🔴 Критично</option>
          <option value="urgent">🟡 Срочно</option>
          <option value="soon">🟠 Скоро</option>
          <option value="ok">🟢 ОК</option>
        </select>
        <select value={filterCat} onChange={e => { setFilterCat(e.target.value); setPage(0); }}
          style={{ padding: '8px 12px', background: 'var(--bg3)', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--text1)', fontSize: 13 }}>
          <option value="all">Все категории</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', color: 'var(--text2)', fontSize: 12 }}>
          {filtered.length} позиций
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,36,37,0.05)' }}>
                <Th k="sku"             label="Артикул" />
                <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text2)', fontSize: 12 }}>Наименование</th>
                <Th k="sales_365d"      label="Продажи 12м" />
                <Th k="days_in_stock"   label="Дней наличия" />
                <Th k="avg_sales"       label="Ср. спрос/день" />
                <Th k="coverage_days"   label="Покрытие" />
                <Th k="lead_time"       label="Срок пост." />
                <Th k="recommended_qty" label="К закупке" />
                <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text2)', fontSize: 12 }}>Причина</th>
                <Th k="purchase_price"  label="Сумма" />
                <Th k="urgency"         label="Статус" />
              </tr>
            </thead>
            <tbody>
              {paged.map((item, idx) => (
                <tr
                  key={item.sku + idx}
                  data-proc-sku={item.sku}
                  style={{
                  borderBottom: '1px solid var(--border)',
                  background: item.urgency === 'critical' ? 'rgba(224,72,72,0.06)' :
                    item.urgency === 'urgent' ? 'rgba(224,138,0,0.04)' : 'transparent',
                }}
                >
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{
                      background: 'rgba(0,167,220,0.15)', border: '1px solid rgba(0,167,220,0.4)',
                      color: '#00a7dc', fontWeight: 800, fontSize: 11,
                      padding: '2px 8px', borderRadius: 4, letterSpacing: '0.05em',
                      fontFamily: 'monospace',
                    }}>{item.sku}</span>
                  </td>
                  <td style={{ padding: '8px 12px', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>
                    {item.name}
                  </td>
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{fmtNum(item.sales_365d)}</td>
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{fmtNum(item.days_in_stock)}</td>
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{fmtNum(item.avg_sales)}</td>
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap',
                    color: item.coverage_days < item.lead_time ? '#e04848' : 'var(--text2)' }}>
                    {item.coverage_days >= 9999 ? '∞' : `${fmtNum(item.coverage_days)} дн`}
                  </td>
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{fmtNum(item.lead_time)} дн</td>
                  <td style={{ padding: '8px 12px', fontWeight: 800,
                    color: item.recommended_qty > 0 ? 'var(--text1)' : 'var(--text2)', whiteSpace: 'nowrap' }}>
                    {item.recommended_qty > 0 ? `${item.recommended_qty} шт` : '—'}
                  </td>
                  <td style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text2)', maxWidth: 240 }}
                    title={item.order_reason}>
                    {item.order_reason || '—'}
                  </td>
                  <td style={{ padding: '8px 12px', color: '#0e9f6e', whiteSpace: 'nowrap' }}>
                    {fmtMoney(item.recommended_qty * item.purchase_price)}
                  </td>
                  <td style={{ padding: '8px 12px' }}><Badge type={item.urgency} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!paged.length && <div style={{ padding: 30, textAlign: 'center', color: 'var(--text2)' }}>Нет данных</div>}
        </div>
      )}

      {/* Пагинация */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 16 }}>
          {Array.from({ length: Math.min(pages, 8) }, (_, i) => (
            <button key={i} onClick={() => setPage(i)} style={{
              padding: '4px 12px', borderRadius: 6, border: '1px solid var(--border)',
              background: page === i ? 'var(--accent)' : 'var(--bg3)',
              color: '#fff', cursor: 'pointer', fontSize: 13,
            }}>{i + 1}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function AnalyticsBlock({ analytics, loading, items, onCreateOrder, creating }) {
  const rows = analytics?.abc_xyz || [];
  const summary = analytics?.summary || {};
  const [selectedSegment, setSelectedSegment] = useState({ abc_qty: 'A', xyz_class: 'X', abc_profit: null });
  const [copyOk, setCopyOk] = useState(false);
  const [createdOrders, setCreatedOrders] = useState([]);
  if (loading) return <Spinner />;
  const abcGroups = ['A', 'B', 'C'];
  const xyzGroups = ['X', 'Y', 'Z'];
  const totalSkus = rows.reduce((s, r) => s + Number(r.skus || 0), 0);
  const totalSales = rows.reduce((s, r) => s + Number(r.sales_365d || 0), 0);
  const totalProfit = rows.reduce((s, r) => s + Number(r.annual_profit || 0), 0);
  const totalToBuy = rows.reduce((s, r) => s + Number(r.to_buy || 0), 0);

  const aggregate = (filterFn) => rows
    .filter(filterFn)
    .reduce((acc, r) => ({
      skus: acc.skus + Number(r.skus || 0),
      sales_365d: acc.sales_365d + Number(r.sales_365d || 0),
      annual_profit: acc.annual_profit + Number(r.annual_profit || 0),
      to_buy: acc.to_buy + Number(r.to_buy || 0),
    }), { skus: 0, sales_365d: 0, annual_profit: 0, to_buy: 0 });

  const matrix = {};
  abcGroups.forEach(a => {
    xyzGroups.forEach(x => {
      matrix[`${a}${x}`] = aggregate(r => r.abc_qty === a && r.xyz_class === x);
    });
  });

  const profitGroups = abcGroups.map(a => ({
    group: a,
    ...aggregate(r => r.abc_profit === a),
  }));

  const actionCards = [
    {
      title: 'AX: ядро ассортимента',
      data: matrix.AX,
      color: '#0e9f6e',
      text: 'Стабильные лидеры. Держать наличие, не допускать нулей, закупать первыми.',
    },
    {
      title: 'AZ/BZ: высокий риск',
      data: aggregate(r => ['A', 'B'].includes(r.abc_qty) && r.xyz_class === 'Z'),
      color: '#e08a00',
      text: 'Спрос рваный. Заказывать осторожно, сверять с сезонностью и текущими продажами.',
    },
    {
      title: 'CZ: хвост ассортимента',
      data: matrix.CZ,
      color: '#e04848',
      text: 'Много шума и нестабильности. Проверять перед закупкой, кандидаты на вывод/заморозку.',
    },
  ];

  const hotSegments = rows
    .filter(r => Number(r.to_buy || 0) > 0)
    .sort((a, b) => Number(b.to_buy || 0) - Number(a.to_buy || 0))
    .slice(0, 8);

  const cellColor = (a, x) => {
    if (a === 'A' && x === 'X') return 'rgba(14,159,110,0.18)';
    if (a === 'A') return 'rgba(14,159,110,0.10)';
    if (a === 'B') return 'rgba(224,138,0,0.10)';
    if (x === 'Z') return 'rgba(224,72,72,0.12)';
    return 'rgba(100,116,139,0.10)';
  };

  const selectedItems = (items || [])
    .filter(i => !selectedSegment?.abc_qty || i.abc_qty === selectedSegment.abc_qty)
    .filter(i => !selectedSegment?.xyz_class || i.xyz_class === selectedSegment.xyz_class)
    .filter(i => !selectedSegment?.abc_profit || i.abc_profit === selectedSegment.abc_profit)
    .sort((a, b) => Number(b.recommended_qty || 0) - Number(a.recommended_qty || 0)
      || Number(b.annual_profit || 0) - Number(a.annual_profit || 0));
  const selectedToBuy = selectedItems.filter(i => Number(i.recommended_qty || 0) > 0);
  const segmentTitle = selectedSegment
    ? `${selectedSegment.abc_qty}${selectedSegment.xyz_class}${selectedSegment.abc_profit ? ` · прибыль ${selectedSegment.abc_profit}` : ''}`
    : 'Сегмент';

  const shareText = selectedToBuy.map(i => [
    i.sku,
    i.name,
    `${fmtNum(i.recommended_qty)} шт`,
    fmtMoney(Number(i.recommended_qty || 0) * Number(i.purchase_price || 0)),
  ].join(' | ')).join('\n');

  const copyList = async () => {
    const text = shareText || selectedItems.map(i => `${i.sku} | ${i.name}`).join('\n');
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    } catch {
      window.prompt('Скопируйте список:', text);
    }
  };

  const createSegmentOrder = async () => {
    if (!selectedToBuy.length || !onCreateOrder) return;
    const res = await onCreateOrder({
      skus: selectedToBuy.map(i => i.sku),
      notes: `ABC/XYZ ${segmentTitle}`,
    }, { stay: true });
    setCreatedOrders(res?.orders || []);
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <KpiCard label="Всего в анализе" value={fmtNum(totalSkus)} color="#00a7dc" />
        <KpiCard label="AX ядро" value={summary.ax_count || matrix.AX?.skus || 0} color="#0e9f6e" sub="держать в наличии" />
        <KpiCard label="CZ хвост" value={summary.cz_count || matrix.CZ?.skus || 0} color="#e04848" sub="проверить/вывод" />
        <KpiCard label="К закупке" value={fmtNum(totalToBuy)} color="#0e9f6e" />
        <KpiCard label="DK всего" value={summary.dk_total || 0} color="#00a7dc" />
        <KpiCard label="DK на вывод" value={summary.dk_candidates || 0} color="#e08a00" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(360px, 1.5fr) minmax(260px, 1fr)', gap: 16, marginBottom: 20 }}>
        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text1)' }}>Матрица ABC / XYZ</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>
                ABC = вклад в продажи, XYZ = стабильность спроса. Чем левее и выше — тем важнее держать наличие.
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', textAlign: 'right' }}>
              Продажи: <b style={{ color: 'var(--text1)' }}>{fmtNum(totalSales)}</b><br />
              Прибыль: <b style={{ color: '#0e9f6e' }}>{fmtMoney(totalProfit)}</b>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '72px repeat(3, 1fr)', gap: 8 }}>
            <div />
            {xyzGroups.map(x => (
              <div key={x} style={{ color: 'var(--text2)', fontSize: 12, fontWeight: 700, textAlign: 'center' }}>
                {x === 'X' ? 'X стабильный' : x === 'Y' ? 'Y средний' : 'Z хаотичный'}
              </div>
            ))}
            {abcGroups.map(a => (
              <React.Fragment key={a}>
                <div style={{ display: 'flex', alignItems: 'center', color: 'var(--text1)', fontWeight: 800 }}>
                  {a === 'A' ? 'A лидеры' : a === 'B' ? 'B середина' : 'C хвост'}
                </div>
                {xyzGroups.map(x => {
                  const cell = matrix[`${a}${x}`] || {};
                  const share = totalSkus ? Math.round((cell.skus || 0) / totalSkus * 100) : 0;
                  const active = selectedSegment?.abc_qty === a && selectedSegment?.xyz_class === x && !selectedSegment?.abc_profit;
                  return (
                    <div
                      key={`${a}${x}`}
                      onClick={() => { setSelectedSegment({ abc_qty: a, xyz_class: x, abc_profit: null }); setCreatedOrders([]); }}
                      style={{
                        background: cellColor(a, x),
                        border: active ? '2px solid var(--accent)' : '1px solid var(--border)',
                        borderRadius: 10, padding: '12px 10px', minHeight: 86,
                        cursor: 'pointer', boxShadow: active ? '0 0 0 3px rgba(0,167,220,0.15)' : 'none',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ color: 'var(--text1)', fontWeight: 900, fontSize: 16 }}>{a}{x}</span>
                        <span style={{ color: 'var(--text2)', fontSize: 11 }}>{share}%</span>
                      </div>
                      <div style={{ color: 'var(--text1)', fontWeight: 800 }}>{fmtNum(cell.skus || 0)} SKU</div>
                      <div style={{ color: 'var(--text2)', fontSize: 11, marginTop: 3 }}>Продажи: {fmtNum(cell.sales_365d || 0)}</div>
                      <div style={{ color: '#0e9f6e', fontSize: 11 }}>Прибыль: {fmtMoney(cell.annual_profit || 0)}</div>
                      {(cell.to_buy || 0) > 0 && (
                        <div style={{ color: '#e08a00', fontSize: 11, fontWeight: 700, marginTop: 4 }}>
                          К закупке: {fmtNum(cell.to_buy)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gap: 12 }}>
          {actionCards.map(card => (
            <div key={card.title} style={{
              background: 'var(--bg3)', border: `1px solid ${card.color}55`,
              borderRadius: 12, padding: 14,
            }}>
              <div style={{ color: card.color, fontSize: 14, fontWeight: 800, marginBottom: 6 }}>{card.title}</div>
              <div style={{ display: 'flex', gap: 14, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text1)', fontWeight: 800 }}>{fmtNum(card.data.skus || 0)} SKU</span>
                <span style={{ color: '#0e9f6e' }}>{fmtMoney(card.data.annual_profit || 0)}</span>
                <span style={{ color: '#e08a00' }}>к закупке {fmtNum(card.data.to_buy || 0)}</span>
              </div>
              <div style={{ color: 'var(--text2)', fontSize: 12, lineHeight: 1.45 }}>{card.text}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 0.8fr) minmax(360px, 1.2fr)', gap: 16 }}>
        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text1)', marginBottom: 12 }}>ABC по прибыли</div>
          {profitGroups.map(g => {
            const pct = totalProfit ? Math.round((g.annual_profit || 0) / totalProfit * 100) : 0;
            const color = g.group === 'A' ? '#0e9f6e' : g.group === 'B' ? '#e08a00' : 'var(--text2)';
            return (
              <div key={g.group} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span style={{ color: 'var(--text1)', fontWeight: 800 }}>Группа {g.group}</span>
                  <span style={{ color, fontWeight: 800 }}>{pct}% прибыли</span>
                </div>
                <div style={{ height: 8, background: 'var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color }} />
                </div>
                <div style={{ color: 'var(--text2)', fontSize: 11, marginTop: 4 }}>
                  {fmtNum(g.skus)} SKU · {fmtMoney(g.annual_profit)} · к закупке {fmtNum(g.to_buy)}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, overflowX: 'auto' }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text1)', marginBottom: 12 }}>Сегменты, где есть позиции к закупке</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Сегмент', 'SKU', 'Продажи 12м', 'Прибыль 12м', 'К закупке', 'Вывод'].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: 'var(--text2)', fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hotSegments.map((row, i) => {
                const segment = `${row.abc_qty}${row.xyz_class}`;
                const active = selectedSegment?.abc_qty === row.abc_qty
                  && selectedSegment?.xyz_class === row.xyz_class
                  && selectedSegment?.abc_profit === row.abc_profit;
                const note = row.abc_qty === 'A' && row.xyz_class === 'X'
                  ? 'закупать без задержки'
                  : row.xyz_class === 'Z'
                    ? 'проверить спрос'
                    : row.abc_qty === 'C'
                      ? 'осторожно'
                      : 'в план закупки';
                return (
                  <tr
                    key={`${segment}-${row.abc_profit}-${i}`}
                    onClick={() => { setSelectedSegment({ abc_qty: row.abc_qty, xyz_class: row.xyz_class, abc_profit: row.abc_profit }); setCreatedOrders([]); }}
                    style={{
                      borderBottom: '1px solid var(--border)',
                      background: active ? 'rgba(0,167,220,0.12)' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <td style={{ padding: '8px 10px', fontWeight: 900, color: row.abc_qty === 'A' ? '#0e9f6e' : row.abc_qty === 'B' ? '#e08a00' : 'var(--text2)' }}>
                      {segment} <span style={{ color: 'var(--text2)', fontWeight: 600 }}>profit {row.abc_profit}</span>
                    </td>
                    <td style={{ padding: '8px 10px' }}>{fmtNum(row.skus)}</td>
                    <td style={{ padding: '8px 10px' }}>{fmtNum(row.sales_365d)}</td>
                    <td style={{ padding: '8px 10px', color: '#0e9f6e' }}>{fmtMoney(row.annual_profit)}</td>
                    <td style={{ padding: '8px 10px', color: '#e08a00', fontWeight: 800 }}>{fmtNum(row.to_buy)}</td>
                    <td style={{ padding: '8px 10px', color: 'var(--text2)' }}>{note}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!hotSegments.length && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text2)' }}>Сейчас нет сегментов с закупкой</div>}
        </div>
      </div>

      <div style={{ marginTop: 16, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text1)' }}>
              Позиции сегмента {segmentTitle}
            </div>
            <div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 3 }}>
              Всего {fmtNum(selectedItems.length)} SKU · к закупке {fmtNum(selectedToBuy.length)} · сумма{' '}
              <b style={{ color: '#0e9f6e' }}>
                {fmtMoney(selectedToBuy.reduce((s, i) => s + Number(i.recommended_qty || 0) * Number(i.purchase_price || 0), 0))}
              </b>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={copyList}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--bg2)', color: copyOk ? '#0e9f6e' : 'var(--text1)', cursor: 'pointer',
                fontWeight: 700, fontSize: 12 }}>
              {copyOk ? '✓ Скопировано' : '📋 Скопировать список'}
            </button>
            <button onClick={createSegmentOrder} disabled={creating || selectedToBuy.length === 0}
              style={{ padding: '8px 14px', borderRadius: 8, border: 'none',
                background: creating || selectedToBuy.length === 0 ? 'var(--bg2)' : 'var(--accent)',
                color: '#fff', cursor: creating || selectedToBuy.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: 800, fontSize: 12 }}>
              {creating ? 'Создаю...' : '📄 Сформировать документ'}
            </button>
          </div>
        </div>

        {createdOrders.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {createdOrders.map(o => (
              <button key={o.id || o.doc_number}
                onClick={() => downloadFile(`/procurement/orders/${o.id}/export/xlsx`, `${o.doc_number}.xlsx`)}
                disabled={!o.id}
                style={{ padding: '7px 12px', borderRadius: 8, border: 'none',
                  background: 'var(--accent)', color: '#fff',
                  cursor: o.id ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 12 }}>
                📊 Скачать {o.category} · {fmtNum(o.total_qty)} шт
              </button>
            ))}
          </div>
        )}

        <div style={{ overflowX: 'auto', maxHeight: 420 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,36,37,0.05)' }}>
                {['SKU', 'Наименование', 'Кат.', 'Остаток', 'Продажи 12м', 'Прибыль', 'К закупке', 'Причина'].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: 'var(--text2)', fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(selectedToBuy.length ? selectedToBuy : selectedItems).slice(0, 120).map(item => (
                <tr key={item.sku} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontWeight: 800, color: '#00a7dc' }}>{item.sku}</td>
                  <td style={{ padding: '8px 10px', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>{item.name}</td>
                  <td style={{ padding: '8px 10px', color: 'var(--accent)', fontWeight: 700 }}>{item.category}</td>
                  <td style={{ padding: '8px 10px' }}>{fmtNum(item.stock)}</td>
                  <td style={{ padding: '8px 10px' }}>{fmtNum(item.sales_365d)}</td>
                  <td style={{ padding: '8px 10px', color: '#0e9f6e' }}>{fmtMoney(item.annual_profit)}</td>
                  <td style={{ padding: '8px 10px', color: item.recommended_qty > 0 ? 'var(--text1)' : 'var(--text2)', fontWeight: 800 }}>
                    {item.recommended_qty > 0 ? `${fmtNum(item.recommended_qty)} шт` : '—'}
                  </td>
                  <td style={{ padding: '8px 10px', color: 'var(--text2)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.order_reason}>
                    {item.order_reason || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!selectedItems.length && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text2)' }}>В сегменте нет позиций</div>}
        </div>
      </div>
    </div>
  );
}

function ActionableAnalyticsBlock({ analytics, loading, items, onCreateOrder, creating }) {
  const [selectedKey, setSelectedKey] = useState('urgent');
  const [copyOk, setCopyOk] = useState(false);
  const [createdOrders, setCreatedOrders] = useState([]);

  if (loading) return <Spinner />;

  const allItems = items || [];
  const toBuy = allItems.filter(i => Number(i.recommended_qty || 0) > 0);
  const urgent = toBuy
    .filter(i => ['critical', 'urgent'].includes(i.urgency))
    .sort((a, b) => Number(b.score || 0) - Number(a.score || 0));
  const stable = toBuy
    .filter(i => !['critical', 'urgent'].includes(i.urgency))
    .filter(i => ['A', 'B'].includes(i.abc_qty) && ['X', 'Y'].includes(i.xyz_class))
    .sort((a, b) => Number(b.annual_profit || 0) - Number(a.annual_profit || 0));
  const review = toBuy
    .filter(i => !urgent.includes(i) && !stable.includes(i))
    .sort((a, b) => Number(b.recommended_qty || 0) - Number(a.recommended_qty || 0));
  const hold = allItems
    .filter(i => Number(i.recommended_qty || 0) <= 0)
    .filter(i => i.dk_candidate || (i.abc_qty === 'C' && i.xyz_class === 'Z'))
    .sort((a, b) => Number(a.sales_365d || 0) - Number(b.sales_365d || 0))
    .slice(0, 50);

  const amount = (list) => list.reduce((s, i) => (
    s + Number(i.recommended_qty || 0) * Number(i.purchase_price || 0)
  ), 0);
  const qty = (list) => list.reduce((s, i) => s + Number(i.recommended_qty || 0), 0);

  const buckets = [
    {
      key: 'urgent',
      title: '1. Закупить срочно',
      subtitle: 'Ноль / закончится раньше поставки',
      color: '#e04848',
      items: urgent,
      action: 'Сразу в заказ поставщику. Это риск потери продаж.',
      canCreate: true,
    },
    {
      key: 'stable',
      title: '2. Закупить планово',
      subtitle: 'A/B + стабильный спрос X/Y',
      color: '#0e9f6e',
      items: stable,
      action: 'Нормальный план закупки. Можно формировать документ без долгих проверок.',
      canCreate: true,
    },
    {
      key: 'review',
      title: '3. Проверить перед закупкой',
      subtitle: 'Рваный спрос, C-группа или спорные позиции',
      color: '#e08a00',
      items: review,
      action: 'Сначала посмотреть причину, остаток и продажи. Закупать выборочно.',
      canCreate: true,
    },
    {
      key: 'hold',
      title: '4. Не закупать / на вывод',
      subtitle: 'Хвост CZ, DK-кандидаты',
      color: 'var(--text2)',
      items: hold,
      action: 'Не добавлять поставщику без ручного решения. Чистить ассортимент.',
      canCreate: false,
    },
  ];

  const selected = buckets.find(b => b.key === selectedKey) || buckets[0];
  const selectedItems = selected.items;
  const createItems = selectedItems.filter(i => Number(i.recommended_qty || 0) > 0);
  const totalTodayAmount = amount([...urgent, ...stable, ...review]);
  const totalTodayQty = qty([...urgent, ...stable, ...review]);

  const explainItem = (item) => {
    if (item.urgency === 'critical') return 'Нулевой остаток';
    if (item.urgency === 'urgent') return 'Не хватит до поставки';
    if (item.xyz_class === 'Z') return 'Спрос нестабильный';
    if (item.abc_qty === 'C') return 'C-группа, закупать осторожно';
    if (item.dk_candidate) return 'DK кандидат на вывод';
    return item.order_reason || 'Плановая закупка';
  };

  const listText = createItems.map(i => [
    i.sku,
    i.name,
    `${fmtNum(i.recommended_qty)} шт`,
    fmtMoney(Number(i.recommended_qty || 0) * Number(i.purchase_price || 0)),
    explainItem(i),
  ].join(' | ')).join('\n');

  const copyList = async () => {
    const text = listText || selectedItems.map(i => `${i.sku} | ${i.name} | ${explainItem(i)}`).join('\n');
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    } catch {
      window.prompt('Скопируйте список:', text);
    }
  };

  const createOrder = async () => {
    if (!selected.canCreate || !createItems.length || !onCreateOrder) return;
    const res = await onCreateOrder({
      skus: createItems.map(i => i.sku),
      notes: selected.title,
    }, { stay: true });
    setCreatedOrders(res?.orders || []);
  };

  return (
    <div>
      <div style={{
        background: 'linear-gradient(135deg, rgba(0,167,220,0.14), rgba(14,159,110,0.08))',
        border: '1px solid rgba(0,167,220,0.35)', borderRadius: 14,
        padding: '16px 18px', marginBottom: 18,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ color: 'var(--text1)', fontSize: 18, fontWeight: 900 }}>Что делать закупщику сегодня</div>
            <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 5, lineHeight: 1.45 }}>
              Сначала закрываем срочные нули и дефицит, потом стабильный спрос, спорные позиции проверяем вручную.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <div><div style={{ color: '#0e9f6e', fontWeight: 900, fontSize: 22 }}>{fmtMoney(totalTodayAmount)}</div><div style={{ color: 'var(--text2)', fontSize: 11 }}>к закупке</div></div>
            <div><div style={{ color: 'var(--text1)', fontWeight: 900, fontSize: 22 }}>{fmtNum(totalTodayQty)} шт</div><div style={{ color: 'var(--text2)', fontSize: 11 }}>количество</div></div>
            <div><div style={{ color: '#e04848', fontWeight: 900, fontSize: 22 }}>{fmtNum(urgent.length)}</div><div style={{ color: 'var(--text2)', fontSize: 11 }}>срочно</div></div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
        {buckets.map(bucket => {
          const active = selectedKey === bucket.key;
          return (
            <button
              key={bucket.key}
              onClick={() => { setSelectedKey(bucket.key); setCreatedOrders([]); }}
              style={{
                textAlign: 'left', padding: 14, borderRadius: 12,
                border: active ? `2px solid ${bucket.color}` : '1px solid var(--border)',
                background: active ? `${bucket.color}18` : 'var(--bg3)',
                cursor: 'pointer', boxShadow: active ? `0 0 0 3px ${bucket.color}22` : 'none',
              }}
            >
              <div style={{ color: bucket.color, fontWeight: 900, fontSize: 14 }}>{bucket.title}</div>
              <div style={{ color: 'var(--text1)', fontWeight: 900, fontSize: 24, marginTop: 8 }}>{fmtNum(bucket.items.length)} SKU</div>
              <div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 4 }}>{bucket.subtitle}</div>
              {bucket.canCreate && (
                <div style={{ color: '#0e9f6e', fontSize: 12, fontWeight: 700, marginTop: 8 }}>
                  {fmtMoney(amount(bucket.items))} · {fmtNum(qty(bucket.items))} шт
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <div style={{ color: selected.color, fontWeight: 900, fontSize: 16 }}>{selected.title}</div>
            <div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 4 }}>{selected.action}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={copyList}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--bg2)', color: copyOk ? '#0e9f6e' : 'var(--text1)',
                cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
              {copyOk ? '✓ Скопировано' : '📋 Скопировать'}
            </button>
            <button onClick={createOrder} disabled={!selected.canCreate || creating || createItems.length === 0}
              style={{ padding: '8px 14px', borderRadius: 8, border: 'none',
                background: !selected.canCreate || creating || createItems.length === 0
                  ? 'var(--bg2)' : 'var(--accent)',
                color: '#fff', cursor: !selected.canCreate || creating || createItems.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: 800, fontSize: 12 }}>
              {creating ? 'Создаю...' : '📄 Сформировать документ'}
            </button>
          </div>
        </div>

        {createdOrders.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {createdOrders.map(o => (
              <button key={o.id || o.doc_number}
                onClick={() => downloadFile(`/procurement/orders/${o.id}/export/xlsx`, `${o.doc_number}.xlsx`)}
                disabled={!o.id}
                style={{ padding: '7px 12px', borderRadius: 8, border: 'none',
                  background: 'var(--accent)', color: '#fff',
                  cursor: o.id ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 12 }}>
                📊 Скачать {o.category} · {fmtNum(o.total_qty)} шт
              </button>
            ))}
          </div>
        )}

        <div style={{ overflowX: 'auto', maxHeight: 560 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,36,37,0.062)' }}>
                {['Решение', 'Артикул', 'Наименование', 'ABC/XYZ', 'Остаток', 'Прод/день', 'Купить', 'Сумма'].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: 'left', color: 'var(--text2)', fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selectedItems.slice(0, 150).map(item => (
                <tr key={item.sku} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 10px', color: selected.color, fontWeight: 800, maxWidth: 190 }}>{explainItem(item)}</td>
                  <td style={{ padding: '9px 10px', fontFamily: 'monospace', color: '#00a7dc', fontWeight: 900 }}>{item.sku}</td>
                  <td style={{ padding: '9px 10px', maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>{item.name}</td>
                  <td style={{ padding: '9px 10px', color: 'var(--text2)' }}>{item.abc_qty}{item.xyz_class} · profit {item.abc_profit}</td>
                  <td style={{ padding: '9px 10px' }}>{fmtNum(item.stock)}</td>
                  <td style={{ padding: '9px 10px' }}>{fmtNum(item.avg_sales)}</td>
                  <td style={{ padding: '9px 10px', color: item.recommended_qty > 0 ? 'var(--text1)' : 'var(--text2)', fontWeight: 900 }}>
                    {item.recommended_qty > 0 ? `${fmtNum(item.recommended_qty)} шт` : '—'}
                  </td>
                  <td style={{ padding: '9px 10px', color: '#0e9f6e', whiteSpace: 'nowrap' }}>
                    {item.recommended_qty > 0 ? fmtMoney(Number(item.recommended_qty || 0) * Number(item.purchase_price || 0)) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!selectedItems.length && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text2)' }}>В этом блоке сейчас нет позиций</div>}
        </div>
      </div>

      <div style={{ marginTop: 12, color: 'var(--text2)', fontSize: 12 }}>
        Справка: A/B/C показывает вклад товара в продажи, X/Y/Z — стабильность спроса. Закупщику важнее всего первые два блока; третий требует проверки; четвёртый обычно не закупаем.
      </div>
    </div>
  );
}

function DkBlock({ items, analytics, loading }) {
  if (loading) return <Spinner />;
  const dkItems = items.filter(i => i.is_dk);
  const candidates = analytics?.dk_candidates || [];
  const stockoutSuspects = analytics?.dk_stockout_suspects || [];

  return (
    <div>
      <div style={{ marginBottom: 16, color: 'var(--text2)', fontSize: 13, lineHeight: 1.5 }}>
        `DK` определяется по первым символам артикула. Список разделён по причине отсутствия продаж, чтобы
        не вывести из ассортимента товары, которые просто не закупались.
        <div style={{ marginTop: 8 }}>
          <span style={{ color: '#e08a00', fontWeight: 700 }}>● На вывод</span> — товар <b>был в наличии</b> (или есть сейчас),
          но почти не продавался → реально нет спроса.
        </div>
        <div style={{ marginTop: 4 }}>
          <span style={{ color: '#00a7dc', fontWeight: 700 }}>● Проверить наличие</span> — продаж нет, <b>но товара почти не было на складе</b>.
          Судить о спросе нельзя: возможно, его просто не закупали (упущенные продажи). Сначала восстановить наличие.
        </div>
      </div>

      <SectionHeader title={`DK — кандидаты на вывод (был в наличии, спроса нет) · ${candidates.length}`} />
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,36,37,0.05)' }}>
              {['SKU', 'Наименование', 'Продажи 12м', 'Прибыль 12м', 'Остаток', 'Дней в налич.', 'ABC qty', 'ABC profit', 'XYZ'].map(h => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text2)', fontSize: 12 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {candidates.map((item, i) => (
              <tr key={item.sku + i} style={{ borderBottom: '1px solid var(--border)', background: 'rgba(224,138,0,0.04)' }}>
                <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{item.sku}</td>
                <td style={{ padding: '8px 12px' }}>{item.name}</td>
                <td style={{ padding: '8px 12px' }}>{fmtNum(item.sales_365d)}</td>
                <td style={{ padding: '8px 12px', color: '#0e9f6e' }}>{fmtMoney(item.annual_profit)}</td>
                <td style={{ padding: '8px 12px' }}>{fmtNum(item.stock)}</td>
                <td style={{ padding: '8px 12px' }}>{fmtNum(item.days_in_stock)}</td>
                <td style={{ padding: '8px 12px' }}>{item.abc_qty}</td>
                <td style={{ padding: '8px 12px' }}>{item.abc_profit}</td>
                <td style={{ padding: '8px 12px' }}>{item.xyz_class}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!candidates.length && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text2)' }}>Кандидатов на вывод нет</div>}
      </div>

      <SectionHeader title={`DK — проверить наличие (нет продаж, но не было на складе) · ${stockoutSuspects.length}`} />
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <div style={{ fontSize: 12, color: '#00a7dc', marginBottom: 8 }}>
          ⚠️ НЕ выводить из ассортимента — сначала закупить и проверить спрос. Низкие продажи здесь — следствие отсутствия товара, а не отсутствия спроса.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,36,37,0.05)' }}>
              {['SKU', 'Наименование', 'Продажи 12м', 'Прибыль 12м', 'Остаток', 'Дней в налич.', 'XYZ'].map(h => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text2)', fontSize: 12 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stockoutSuspects.map((item, i) => (
              <tr key={item.sku + i} style={{ borderBottom: '1px solid var(--border)', background: 'rgba(0,167,220,0.05)' }}>
                <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{item.sku}</td>
                <td style={{ padding: '8px 12px' }}>{item.name}</td>
                <td style={{ padding: '8px 12px' }}>{fmtNum(item.sales_365d)}</td>
                <td style={{ padding: '8px 12px', color: '#0e9f6e' }}>{fmtMoney(item.annual_profit)}</td>
                <td style={{ padding: '8px 12px', color: '#e04848', fontWeight: 700 }}>{fmtNum(item.stock)}</td>
                <td style={{ padding: '8px 12px' }}>{fmtNum(item.days_in_stock)}</td>
                <td style={{ padding: '8px 12px' }}>{item.xyz_class}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!stockoutSuspects.length && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text2)' }}>Таких позиций нет</div>}
      </div>

      <SectionHeader title="DK — текущая закупка" />
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,36,37,0.05)' }}>
              {['SKU', 'Наименование', 'Покрытие', 'Срок пост.', 'К закупке', 'Причина'].map(h => (
                <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text2)', fontSize: 12 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dkItems.filter(i => i.recommended_qty > 0).map((item, i) => (
              <tr key={item.sku + i} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{item.sku}</td>
                <td style={{ padding: '8px 12px' }}>{item.name}</td>
                <td style={{ padding: '8px 12px' }}>{item.coverage_days >= 9999 ? '∞' : `${fmtNum(item.coverage_days)} дн`}</td>
                <td style={{ padding: '8px 12px' }}>{fmtNum(item.lead_time)} дн</td>
                <td style={{ padding: '8px 12px', fontWeight: 800 }}>{fmtNum(item.recommended_qty)} шт</td>
                <td style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text2)' }}>{item.order_reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!dkItems.filter(i => i.recommended_qty > 0).length && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text2)' }}>Сейчас DK-позиций к закупке нет</div>}
      </div>
    </div>
  );
}

// ─── ТОП-20 ──────────────────────────────────────────────────────────────────
function Top20Block({ items }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,36,37,0.05)' }}>
            {['Артикул', 'Наименование', 'К закупке', 'Сумма', 'Статус'].map(h => (
              <th key={h} style={{ padding: '8px 12px', textAlign: 'left',
                color: 'var(--text2)', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={item.sku} style={{ borderBottom: '1px solid var(--border)',
              background: idx < 3 ? 'rgba(0,167,220,0.05)' : 'transparent' }}>
              <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                <span style={{
                  background: 'rgba(0,167,220,0.15)', border: '1px solid rgba(0,167,220,0.4)',
                  color: '#00a7dc', fontWeight: 800, fontSize: 11,
                  padding: '2px 8px', borderRadius: 4, letterSpacing: '0.05em',
                }}>{item.sku}</span>
              </td>
              <td style={{ padding: '8px 12px', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>
                {idx < 3 && <span style={{ marginRight: 6 }}>{['🥇','🥈','🥉'][idx]}</span>}
                {item.name}
              </td>
              <td style={{ padding: '8px 12px', fontWeight: 800, color: 'var(--text1)', whiteSpace: 'nowrap' }}>{item.recommended_qty} шт</td>
              <td style={{ padding: '8px 12px', color: '#0e9f6e', whiteSpace: 'nowrap' }}>
                {fmtMoney(item.recommended_qty * item.purchase_price)}
              </td>
              <td style={{ padding: '8px 12px' }}><Badge type={item.urgency} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Категории ───────────────────────────────────────────────────────────────
function CategoriesBlock({ data }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      {data.map(cat => (
        <div key={cat.category} style={{
          background: 'var(--bg3)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '14px 16px',
          borderTop: cat.critical > 0 ? '3px solid #e04848' : cat.urgent > 0 ? '3px solid #e08a00' : '3px solid var(--border)',
        }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--accent)', marginBottom: 4 }}>
            {cat.category}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>
            {cat.total_skus} SKU
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {cat.critical > 0 && <span style={{ background: '#e0484822', color: '#e04848',
              borderRadius: 5, padding: '2px 6px', fontSize: 11, fontWeight: 700 }}>
              🔴 {cat.critical} крит.
            </span>}
            {cat.urgent > 0 && <span style={{ background: '#e08a0022', color: '#e08a00',
              borderRadius: 5, padding: '2px 6px', fontSize: 11, fontWeight: 700 }}>
              🟡 {cat.urgent} срочно
            </span>}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text1)' }}>
            К закупке: {cat.to_buy} позиций
          </div>
          <div style={{ fontSize: 12, color: '#0e9f6e', marginTop: 2 }}>
            {fmtMoney(cat.rec_amount)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Документы закупки ───────────────────────────────────────────────────────
// ─── Сверка каталога сайта с 1С ──────────────────────────────────────────────
const BUCKETS = [
  { id: 'under_order', label: 'Под заказ (нет в наличии)', color: '#e08a00',
    hint: 'Позиции, которых нет в наличии на сайте — кандидаты на закупку у поставщика.' },
  { id: 'gap', label: 'На сайте есть, в 1С нет', color: '#e04848',
    hint: 'Товары продаются на сайте, но их карточек нет в 1С — Закупщик их не видел. Это и есть пропавшие позиции.' },
  { id: 'onec_only', label: 'В 1С есть, на сайте нет', color: '#00a7dc',
    hint: 'Заведены в 1С, но на сайте отсутствуют (сняты с витрины / скрыты).' },
];

function SiteReconcileBlock({ showToast, onOrderCreated }) {
  const [summary, setSummary] = useState(null);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selCat, setSelCat] = useState('');
  const [bucket, setBucket] = useState('under_order');
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const pollRef = useRef(null);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/procurement/site/reconcile');
      setSummary(r.data?.summary || null);
      setCats(r.data?.categories || []);
    } catch (_) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const loadRows = useCallback(async (cat, bkt) => {
    if (!cat) return;
    setRowsLoading(true);
    setSel(new Set());
    try {
      const r = await api.get(`/procurement/site/reconcile?category=${encodeURIComponent(cat)}&bucket=${bkt}`);
      setRows(r.data?.items || []);
    } catch (_) { setRows([]); }
    finally { setRowsLoading(false); }
  }, []);

  const openCat = (cat, bkt) => {
    setSelCat(cat); setBucket(bkt); setSearch('');
    loadRows(cat, bkt);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post('/procurement/site/sync?background=true');
      showToast('🌐 Синхронизация каталога сайта запущена (1–3 мин)');
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get('/procurement/site/status');
          if (!s.data?.running) {
            clearInterval(pollRef.current);
            setSyncing(false);
            showToast(`✅ Каталог сайта обновлён: ${s.data?.stored || 0} позиций`);
            loadSummary();
            if (selCat) loadRows(selCat, bucket);
          }
        } catch (_) { /* ignore */ }
      }, 5000);
    } catch (e) {
      setSyncing(false);
      showToast('❌ Не удалось запустить синхронизацию');
    }
  };

  const filteredRows = rows.filter(r => !search ||
    (r.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.article || '').toLowerCase().includes(search.toLowerCase()));

  const toggle = (art) => {
    setSel(prev => {
      const n = new Set(prev);
      if (n.has(art)) n.delete(art); else n.add(art);
      return n;
    });
  };
  const toggleAll = () => {
    if (sel.size === filteredRows.length) setSel(new Set());
    else setSel(new Set(filteredRows.map(r => r.article)));
  };

  const copyList = () => {
    const list = (sel.size ? filteredRows.filter(r => sel.has(r.article)) : filteredRows);
    const text = list.map(r => `${r.article_raw || r.article}\t${r.name || ''}\t${r.site_price || ''}`).join('\n');
    navigator.clipboard.writeText(text).then(
      () => showToast(`Скопировано ${list.length} позиций`),
      () => showToast('Не удалось скопировать')
    );
  };

  const createDoc = async () => {
    const arts = sel.size ? [...sel] : filteredRows.map(r => r.article);
    if (!arts.length) { showToast('Нет позиций'); return; }
    setCreating(true);
    try {
      const r = await api.post('/procurement/site/order', { articles: arts, notes: `Сверка сайт↔1С: ${selCat}/${bucket}` });
      if (!r.data?.ok) { showToast(r.data?.message || 'Не удалось создать документ'); return; }
      showToast(`✅ Создано ${r.data.created} документ(ов) из ${arts.length} позиций`);
      if (onOrderCreated) onOrderCreated();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Ошибка';
      showToast(`Ошибка: ${String(msg).slice(0, 160)}`);
    } finally { setCreating(false); }
  };

  return (
    <div>
      {/* Панель синхронизации */}
      <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)',
        borderRadius: 10, padding: '14px 16px', marginBottom: 16, display: 'flex',
        justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: 'var(--text2)' }}>
          Сравнивает живой каталог <b style={{color:'var(--text1)'}}>keyacademy.ru</b> с данными 1С.
          {summary?.last_sync ? <> Последняя синхронизация: <b style={{color:'var(--text1)'}}>{summary.last_sync}</b>.</>
            : <> Каталог сайта ещё не загружен — нажмите «Синхронизировать».</>}
        </div>
        <button onClick={handleSync} disabled={syncing}
          style={{ padding: '8px 18px', borderRadius: 8, border: 'none',
            background: syncing ? 'var(--bg2)' : 'var(--accent)',
            color: '#fff', cursor: syncing ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13,
            whiteSpace: 'nowrap' }}>
          {syncing ? '⏳ Синхронизация...' : '🔄 Синхронизировать сайт'}
        </button>
      </div>

      {loading ? <Spinner /> : !summary ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text2)' }}>Нет данных.</div>
      ) : (
        <>
          {/* Сводка */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0,1fr))', gap: 10, marginBottom: 18 }}>
            <KpiCard label="Позиций на сайте" value={fmtNum(summary.site_total)} icon="🌐" color="#fff" />
            <KpiCard label="В наличии" value={fmtNum(summary.site_in_stock)} icon="✅" color="#0e9f6e" />
            <KpiCard label="Под заказ" value={fmtNum(summary.site_under_order)} icon="🟠" color="#e08a00" />
            <KpiCard label="Сошлось с 1С" value={fmtNum(summary.matched_1c)} icon="🔗" color="#00a7dc" />
            <KpiCard label="Нет в 1С (пробел)" value={fmtNum(summary.gap)} icon="⚠️"
              color={summary.gap > 0 ? '#e04848' : '#0e9f6e'} />
            <KpiCard label="Только в 1С" value={fmtNum(summary.onec_only)} icon="📦" color="var(--text2)" />
          </div>

          {summary.site_total === 0 && (
            <div style={{ background: '#e08a0022', border: '1px solid #e08a00', borderRadius: 10,
              padding: '12px 16px', marginBottom: 16, color: '#e08a00', fontSize: 13 }}>
              Каталог сайта пуст. Нажмите «Синхронизировать сайт» — обход займёт 1–3 минуты.
            </div>
          )}

          {/* Таблица категорий */}
          <SectionHeader title="По категориям — где расхождения" />
          <div style={{ overflowX: 'auto', marginBottom: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,36,37,0.05)' }}>
                  {['Категория', 'На сайте', 'Под заказ', 'Нет в 1С', 'Только в 1С', 'Действия'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text2)', fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cats.map(c => (
                  <tr key={c.category} style={{ borderBottom: '1px solid var(--border)',
                    background: selCat === c.category ? 'rgba(0,167,220,0.12)' : 'transparent' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 700, color: 'var(--accent)' }}>{c.category}</td>
                    <td style={{ padding: '8px 12px' }}>{fmtNum(c.site_total)}</td>
                    <td style={{ padding: '8px 12px', color: c.site_under_order ? '#e08a00' : 'var(--text2)' }}>{fmtNum(c.site_under_order)}</td>
                    <td style={{ padding: '8px 12px', color: c.gap ? '#e04848' : 'var(--text2)', fontWeight: c.gap ? 700 : 400 }}>{fmtNum(c.gap)}</td>
                    <td style={{ padding: '8px 12px', color: 'var(--text2)' }}>{fmtNum(c.onec_only)}</td>
                    <td style={{ padding: '8px 12px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button onClick={() => openCat(c.category, 'under_order')}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e08a0055',
                          background: '#e08a0022', color: '#e08a00', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                        Под заказ
                      </button>
                      <button onClick={() => openCat(c.category, 'gap')}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e0484855',
                          background: '#e0484822', color: '#e04848', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                        Нет в 1С
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Детальный список выбранной категории/бакета */}
          {selCat && (
            <div style={{ marginTop: 18 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                {BUCKETS.map(b => (
                  <button key={b.id} onClick={() => { setBucket(b.id); loadRows(selCat, b.id); }}
                    style={{ padding: '6px 12px', borderRadius: 7, border: `1px solid ${b.color}55`,
                      background: bucket === b.id ? b.color : `${b.color}18`,
                      color: bucket === b.id ? '#fff' : b.color, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                    {b.label}
                  </button>
                ))}
                <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text1)', marginLeft: 6 }}>{selCat}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>
                {BUCKETS.find(b => b.id === bucket)?.hint}
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="🔍 Поиск..."
                  style={{ flex: 1, minWidth: 180, padding: '7px 12px', background: 'var(--bg3)',
                    border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text1)', fontSize: 13 }} />
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>
                  {filteredRows.length} позиций{sel.size ? `, выбрано ${sel.size}` : ''}
                </span>
                <button onClick={copyList}
                  style={{ padding: '7px 14px', borderRadius: 7, border: '1px solid var(--border)',
                    background: 'var(--bg3)', color: 'var(--text1)', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                  📋 Скопировать
                </button>
                <button onClick={createDoc} disabled={creating || filteredRows.length === 0}
                  style={{ padding: '7px 16px', borderRadius: 7, border: 'none',
                    background: creating ? 'var(--bg2)' : 'var(--accent)',
                    color: '#fff', cursor: creating ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 }}>
                  {creating ? '⏳ Создаётся...' : `🛒 Сформировать документ${sel.size ? ` (${sel.size})` : ''}`}
                </button>
              </div>

              {rowsLoading ? <Spinner /> : filteredRows.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text2)' }}>
                  Нет позиций в этой группе.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,36,37,0.05)' }}>
                        <th style={{ padding: '8px 10px', textAlign: 'left' }}>
                          <input type="checkbox" checked={sel.size > 0 && sel.size === filteredRows.length} onChange={toggleAll} />
                        </th>
                        {['Артикул', 'Наименование', 'Сайт ₽', 'Наличие сайт', 'В 1С', 'Остаток 1С', 'Продажи 12м'].map(h => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: 'var(--text2)', fontSize: 12 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.slice(0, 300).map((r, i) => (
                        <tr key={r.article + i} style={{ borderBottom: '1px solid var(--border)',
                          background: sel.has(r.article) ? 'rgba(14,159,110,0.10)' : 'transparent' }}>
                          <td style={{ padding: '6px 10px' }}>
                            <input type="checkbox" checked={sel.has(r.article)} onChange={() => toggle(r.article)} />
                          </td>
                          <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontWeight: 700 }}>
                            {r.url ? <a href={`https://keyacademy.ru${r.url}`} target="_blank" rel="noreferrer"
                              style={{ color: 'var(--accent)', textDecoration: 'none' }}>{r.article_raw || r.article}</a>
                              : (r.article_raw || r.article)}
                          </td>
                          <td style={{ padding: '6px 10px', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name || '—'}</td>
                          <td style={{ padding: '6px 10px', color: '#0e9f6e' }}>{r.site_price ? fmtMoney(r.site_price) : '—'}</td>
                          <td style={{ padding: '6px 10px' }}>
                            {r.site_in_stock === null ? '—' : r.site_in_stock
                              ? <span style={{ color: '#0e9f6e' }}>в наличии</span>
                              : <span style={{ color: '#e08a00' }}>под заказ</span>}
                          </td>
                          <td style={{ padding: '6px 10px' }}>
                            {r.in_1c ? <span style={{ color: '#0e9f6e' }}>✓</span> : <span style={{ color: '#e04848' }}>нет</span>}
                          </td>
                          <td style={{ padding: '6px 10px', color: 'var(--text2)' }}>{r.onec_stock == null ? '—' : fmtNum(r.onec_stock)}</td>
                          <td style={{ padding: '6px 10px', color: 'var(--text2)' }}>{r.sales_365d == null ? '—' : fmtNum(r.sales_365d)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredRows.length > 300 && (
                    <div style={{ padding: 10, textAlign: 'center', color: 'var(--text2)', fontSize: 12 }}>
                      Показаны первые 300 из {filteredRows.length}. Уточните поиск или сформируйте документ по всем.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OrdersBlock({ orders, onCreateOrder, creating }) {
  const [selectedCat, setSelectedCat] = useState('');
  const [notes, setNotes] = useState('');

  return (
    <div>
      {/* Форма создания */}
      <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)',
        borderRadius: 10, padding: '16px', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: 'var(--text1)' }}>
          Сформировать документ закупки
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input
            value={selectedCat}
            onChange={e => setSelectedCat(e.target.value.toUpperCase())}
            placeholder="Категория (PR = PR+XH+KD+AU)"
            maxLength={2}
            style={{ width: 180, padding: '8px 12px', background: 'var(--bg2)',
              border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text1)', fontSize: 13 }}
          />
          <input
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Примечание (необязательно)"
            style={{ flex: 1, minWidth: 200, padding: '8px 12px', background: 'var(--bg2)',
              border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text1)', fontSize: 13 }}
          />
          <button
            onClick={() => onCreateOrder({ category: selectedCat || null, notes })}
            disabled={creating}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none',
              background: creating ? 'var(--bg2)' : 'var(--accent)',
              color: '#fff', cursor: creating ? 'not-allowed' : 'pointer',
              fontWeight: 700, fontSize: 13 }}>
            {creating ? '⏳ Создаётся...' : '📋 Сформировать'}
          </button>
        </div>
        <div style={{ marginTop: 8, color: 'var(--text2)', fontSize: 12 }}>
          Важно: ввод `PR` формирует общий документ по оборудованию: PR, XH, KD, AU.
        </div>
      </div>

      {/* Список документов */}
      {orders.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text2)' }}>
          Нет документов. Нажмите «Сформировать».
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,36,37,0.05)' }}>
                {['Номер', 'Категория', 'Позиций', 'Кол-во', 'Сумма', 'Статус', 'Дата', 'Экспорт'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left',
                    color: 'var(--text2)', fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{o.doc_number}</td>
                  <td style={{ padding: '8px 12px', fontWeight: 700, color: 'var(--accent)' }}>{o.category || 'ALL'}</td>
                  <td style={{ padding: '8px 12px' }}>{(o.items || []).length}</td>
                  <td style={{ padding: '8px 12px', fontWeight: 600 }}>{fmtNum(o.total_qty)} шт</td>
                  <td style={{ padding: '8px 12px', color: '#0e9f6e' }}>{fmtMoney(o.total_amount)}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{
                      background: o.status === 'draft' ? '#64748b33' : o.status === 'sent' ? '#00a7dc33' : '#0e9f6e33',
                      color: o.status === 'draft' ? 'var(--text2)' : o.status === 'sent' ? '#00a7dc' : '#0e9f6e',
                      borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                    }}>{o.status}</span>
                  </td>
                  <td style={{ padding: '8px 12px', color: 'var(--text2)', fontSize: 12 }}>
                    {o.created_at ? new Date(o.created_at).toLocaleDateString('ru-RU') : '—'}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <ExcelBtn orderId={o.id} docNumber={o.doc_number || `order-${o.id}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Приёмка ─────────────────────────────────────────────────────────────────
function ReceiptBlock({ receipts, onCreateReceipt }) {
  const [supplier, setSupplier] = useState('');
  const [notes, setNotes] = useState('');
  const [rawText, setRawText] = useState('');
  const [parsed, setParsed] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const parseCsv = (text) => {
    const lines = text.trim().split('\n').filter(Boolean);
    const items = [];
    for (const line of lines) {
      const parts = line.split(/[;\t,]/);
      if (parts.length >= 3) {
        items.push({
          sku: parts[0].trim(),
          name: parts[1]?.trim() || '',
          ordered_qty: parseFloat(parts[2]) || 0,
          received_qty: parseFloat(parts[3] ?? parts[2]) || 0,
          price: parseFloat(parts[4]) || 0,
        });
      }
    }
    return items;
  };

  const handleParse = () => {
    const items = parseCsv(rawText);
    setParsed(items);
    setError(items.length ? '' : 'Не удалось распознать — формат: SKU;Наименование;Заказ;Получено;Цена');
  };

  const handleSave = async () => {
    if (!parsed.length) return;
    setSaving(true);
    try {
      await onCreateReceipt({ supplier, items: parsed, notes });
      setRawText('');
      setParsed([]);
      setSupplier('');
      setNotes('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: 'var(--text1)' }}>
          Новая приёмка — вставьте данные поставщика
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <input value={supplier} onChange={e => setSupplier(e.target.value)}
            placeholder="Поставщик"
            style={{ flex: 1, minWidth: 200, padding: '8px 12px', background: 'var(--bg2)',
              border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text1)', fontSize: 13 }} />
          <input value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Примечание"
            style={{ flex: 1, minWidth: 200, padding: '8px 12px', background: 'var(--bg2)',
              border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text1)', fontSize: 13 }} />
        </div>
        <textarea
          value={rawText}
          onChange={e => setRawText(e.target.value)}
          placeholder={'Формат: SKU;Наименование;Заказано;Получено;Цена\nНапример:\nAB001;Товар А;10;10;500\nAB002;Товар Б;5;4;800'}
          rows={6}
          style={{ width: '100%', padding: '10px 12px', background: 'var(--bg2)',
            border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text1)', fontSize: 12,
            fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }}
        />
        {error && <div style={{ color: '#e04848', fontSize: 12, marginTop: 6 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
          <button onClick={handleParse}
            style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)',
              background: 'var(--bg2)', color: 'var(--text1)', cursor: 'pointer', fontSize: 13 }}>
            🔍 Разобрать
          </button>
          {parsed.length > 0 && (
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '8px 20px', borderRadius: 8, border: 'none',
                background: 'var(--accent)',
                color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
              {saving ? '⏳ Сохраняется...' : `✓ Принять (${parsed.length} поз.)`}
            </button>
          )}
        </div>

        {/* Предпросмотр */}
        {parsed.length > 0 && (
          <div style={{ marginTop: 12, overflowX: 'auto' }}>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
              Предпросмотр: {parsed.length} позиций
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['SKU', 'Наименование', 'Заказано', 'Получено', 'Цена', 'Расхождение'].map(h => (
                    <th key={h} style={{ padding: '6px 10px', color: 'var(--text2)', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parsed.map((p, i) => {
                  const diff = p.received_qty - p.ordered_qty;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)',
                      background: diff !== 0 ? 'rgba(224,72,72,0.05)' : 'transparent' }}>
                      <td style={{ padding: '5px 10px', fontFamily: 'monospace' }}>{p.sku}</td>
                      <td style={{ padding: '5px 10px' }}>{p.name}</td>
                      <td style={{ padding: '5px 10px' }}>{p.ordered_qty}</td>
                      <td style={{ padding: '5px 10px', fontWeight: diff !== 0 ? 700 : 400,
                        color: diff < 0 ? '#e04848' : diff > 0 ? '#e08a00' : '#0e9f6e' }}>
                        {p.received_qty}
                      </td>
                      <td style={{ padding: '5px 10px', color: 'var(--text2)' }}>{fmtMoney(p.price)}</td>
                      <td style={{ padding: '5px 10px', color: diff !== 0 ? '#e04848' : '#0e9f6e' }}>
                        {diff !== 0 ? `${diff > 0 ? '+' : ''}${diff}` : '✓'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* История приёмок */}
      {receipts.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,36,37,0.05)' }}>
                {['#', 'Поставщик', 'Позиций', 'Заказано', 'Получено', 'Расхождений', 'Статус', 'Дата'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text2)', fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {receipts.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 12px', color: 'var(--text2)' }}>#{r.id}</td>
                  <td style={{ padding: '8px 12px' }}>{r.supplier || '—'}</td>
                  <td style={{ padding: '8px 12px' }}>{r.items_count ?? (r.items || []).length}</td>
                  <td style={{ padding: '8px 12px', color: 'var(--text2)' }}>{fmtMoney(r.total_ordered)}</td>
                  <td style={{ padding: '8px 12px', color: '#0e9f6e' }}>{fmtMoney(r.total_received)}</td>
                  <td style={{ padding: '8px 12px', color: (r.discrepancies || []).length > 0 ? '#e08a00' : '#0e9f6e' }}>
                    {(r.discrepancies || []).length}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{
                      background: r.status === 'done' ? '#0e9f6e22' : r.status === 'partial' ? '#e08a0022' : '#64748b22',
                      color: r.status === 'done' ? '#0e9f6e' : r.status === 'partial' ? '#e08a00' : 'var(--text2)',
                      borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                    }}>{r.status}</span>
                  </td>
                  <td style={{ padding: '8px 12px', color: 'var(--text2)', fontSize: 12 }}>
                    {r.created_at ? new Date(r.created_at).toLocaleDateString('ru-RU') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Контроль закупщика ───────────────────────────────────────────────────────
function ControlBlock({ control, onSaveActuals, orders }) {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [rawActuals, setRawActuals] = useState('');
  const [saving, setSaving] = useState(false);
  const [hint, setHint] = useState('');
  const [localControl, setLocalControl] = useState(control);

  useEffect(() => { setLocalControl(control); }, [control]);

  // Авто-заполнение из созданных документов за выбранный период
  const handleAutoFill = () => {
    const periodOrders = (orders || []).filter(o => {
      const dateStr = (o.created_at || '').toString();
      const docNum  = o.doc_number || '';
      // created_at = "2026-03-26T..." → contains "2026-03"
      // doc_number = "ZAK-202603261958-XH" → contains "202603"
      return dateStr.includes(period) || docNum.includes(period.replace('-', ''));
    });
    if (!periodOrders.length) {
      setHint(`Нет документов за ${period}. Введите данные вручную.`);
      return;
    }
    const skuMap = {};
    periodOrders.forEach(order => {
      (order.items || []).forEach(item => {
        const sku = item.sku;
        const qty = parseFloat(item.recommended_qty) || 0;
        if (sku) skuMap[sku] = (skuMap[sku] || 0) + qty;
      });
    });
    const lines = Object.entries(skuMap).map(([sku, qty]) => `${sku};${qty}`).join('\n');
    setRawActuals(lines);
    setHint(`Загружено ${Object.keys(skuMap).length} позиций из ${periodOrders.length} документа(ов). Нажмите «Рассчитать».`);
  };

  const handleSave = async () => {
    const lines = rawActuals.trim().split('\n').filter(Boolean);
    const actuals = lines.map(l => {
      const [sku, qty] = l.split(/[;\t,]/);
      return { sku: sku?.trim(), actual_qty: parseFloat(qty) || 0 };
    }).filter(a => a.sku);

    if (!actuals.length) {
      setHint('Введите данные фактической закупки или нажмите «Заполнить из документов».');
      return;
    }
    setHint('');
    setSaving(true);
    try {
      const result = await onSaveActuals({ period, actuals });
      setLocalControl(result);
    } catch {
      setHint('Ошибка при расчёте. Попробуйте ещё раз.');
    } finally {
      setSaving(false);
    }
  };

  const devs = localControl?.deviations || [];
  const understock = devs.filter(d => d.delta_type === 'understock');
  const overstock  = devs.filter(d => d.delta_type === 'overstock');

  return (
    <div>
      {/* KPI контроля */}
      {localControl && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
          <KpiCard label="Попадание в план" value={`${localControl.hit_rate_pct}%`}
            color={localControl.hit_rate_pct >= 80 ? '#0e9f6e' : localControl.hit_rate_pct >= 60 ? '#e08a00' : '#e04848'} />
          <KpiCard label="Позиций оценено" value={localControl.total_skus} color="#00a7dc" />
          <KpiCard label="Риск (недозакуп)" value={fmtMoney(localControl.money_at_risk)} color="#e04848" icon="❌" />
          <KpiCard label="Заморожено" value={fmtMoney(localControl.money_frozen)} color="#e08a00" icon="🧊" />
          <KpiCard label="Недозакупов" value={understock.length} color="#e04848" icon="↘" />
          <KpiCard label="Перезакупов" value={overstock.length} color="#e08a00" icon="↗" />
        </div>
      )}

      {/* Ввод факта */}
      <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text1)', marginBottom: 10 }}>
          Ввод фактической закупки
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ color: 'var(--text2)', fontSize: 12 }}>Период:</div>
          <input type="month" value={period} onChange={e => { setPeriod(e.target.value); setHint(''); }}
            style={{ padding: '7px 10px', background: 'var(--bg2)', border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--text1)', fontSize: 13 }} />
          <button onClick={handleAutoFill}
            style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)',
              background: 'var(--bg2)', color: '#00a7dc', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            📋 Заполнить из документов
          </button>
        </div>
        <textarea
          value={rawActuals}
          onChange={e => { setRawActuals(e.target.value); setHint(''); }}
          placeholder={'Формат: SKU;Количество\nНапример:\nAB001;12\nAB002;5'}
          rows={5}
          style={{ width: '100%', padding: '10px 12px', background: 'var(--bg2)',
            border: `1px solid ${hint && !rawActuals ? '#e04848' : 'var(--border)'}`,
            borderRadius: 8, color: 'var(--text1)', fontSize: 12,
            fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }}
        />
        {hint && (
          <div style={{ fontSize: 12, color: rawActuals ? '#0e9f6e' : '#e08a00', marginTop: 6 }}>
            {hint}
          </div>
        )}
        <button onClick={handleSave} disabled={saving}
          style={{ marginTop: 10, padding: '8px 20px', borderRadius: 8, border: 'none',
            background: saving ? 'var(--bg2)' : 'var(--accent)',
            color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
          {saving ? '⏳ Считается...' : '📊 Рассчитать контроль'}
        </button>
      </div>

      {/* Отклонения */}
      {devs.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,36,37,0.05)' }}>
                {['SKU', 'Наименование', 'Рекомендация', 'Факт', 'Отклонение', 'Тип', 'Риск / Заморозка'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text2)', fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {devs.map((d, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)',
                  background: d.delta_type === 'understock' ? 'rgba(224,72,72,0.05)' :
                    d.delta_type === 'overstock' ? 'rgba(224,138,0,0.04)' : 'transparent' }}>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{d.sku}</td>
                  <td style={{ padding: '8px 12px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${d.sku||''} ${d.name}`}>
                    {d.sku && <span style={{
                      display: 'inline-block', marginRight: 6,
                      background: 'rgba(0,167,220,0.15)', border: '1px solid rgba(0,167,220,0.4)',
                      color: '#00a7dc', fontWeight: 800, fontSize: 10,
                      padding: '1px 6px', borderRadius: 4, letterSpacing: '0.05em',
                      whiteSpace: 'nowrap', verticalAlign: 'middle',
                    }}>{d.sku}</span>}
                    <span style={{ verticalAlign: 'middle' }}>{d.name}</span>
                  </td>
                  <td style={{ padding: '8px 12px', color: 'var(--text2)' }}>{fmtNum(d.recommended_qty)}</td>
                  <td style={{ padding: '8px 12px', fontWeight: 600 }}>{fmtNum(d.actual_qty)}</td>
                  <td style={{ padding: '8px 12px', fontWeight: 700,
                    color: d.delta < 0 ? '#e04848' : d.delta > 0 ? '#e08a00' : '#0e9f6e' }}>
                    {d.delta > 0 ? '+' : ''}{fmtNum(d.delta)}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{
                      background: `${DELTA_COLOR[d.delta_type]}22`,
                      color: DELTA_COLOR[d.delta_type],
                      borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                    }}>{DELTA_LABEL[d.delta_type]}</span>
                  </td>
                  <td style={{ padding: '8px 12px', color: d.delta_type === 'understock' ? '#e04848' : '#e08a00' }}>
                    {d.money_at_risk > 0 ? fmtMoney(d.money_at_risk) :
                     d.money_frozen > 0  ? fmtMoney(d.money_frozen)  : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Главная страница ─────────────────────────────────────────────────────────
export default function Procurement() {
  const [searchParams] = useSearchParams();
  const focusSku = (searchParams.get('sku') || '').trim();
  const [tab, setTab] = useState('kpi');

  useLayoutEffect(() => {
    if (focusSku) setTab('items');
  }, [focusSku]);
  const [kpi, setKpi] = useState(null);
  const [items, setItems] = useState([]);
  const [top20, setTop20] = useState([]);
  const [categories, setCategories] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [control, setControl] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [histLoading, setHistLoading] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [kpiR, itemsR, top20R, catsR, analyticsR, ordersR, receiptsR, statusR] = await Promise.all([
        api.get('/procurement/kpi').catch(() => ({ data: null })),
        api.get('/procurement/items').catch(() => ({ data: [] })),
        api.get('/procurement/top20').catch(() => ({ data: [] })),
        api.get('/procurement/categories').catch(() => ({ data: [] })),
        api.get('/procurement/analytics').catch(() => ({ data: null })),
        api.get('/procurement/orders').catch(() => ({ data: [] })),
        api.get('/procurement/receipts').catch(() => ({ data: [] })),
        api.get('/procurement/status').catch(() => ({ data: null })),
      ]);

      // Бэкенд может вернуть массив или {loading:true, items:[]}
      const normalizeList = (d) => Array.isArray(d) ? d : (d && Array.isArray(d.items) ? d.items : []);
      const normalizeKpi  = (d) => (d && !Array.isArray(d) && !d.loading) ? d : null;

      setKpi(normalizeKpi(kpiR.data));
      setItems(normalizeList(itemsR.data));
      setTop20(normalizeList(top20R.data));
      setCategories(normalizeList(catsR.data));
      setAnalytics(analyticsR.data && !analyticsR.data.loading ? analyticsR.data : null);
      setOrders(normalizeList(ordersR.data));
      setReceipts(normalizeList(receiptsR.data));
      setStatus(statusR.data);

      // Контроль текущего месяца
      const period = new Date().toISOString().slice(0, 7);
      const ctrlR = await api.get(`/procurement/control/${period}`).catch(() => ({ data: null }));
      setControl(ctrlR.data);
    } catch (e) {
      setError('Ошибка загрузки данных. Проверьте подключение к 1С.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Автополинг когда данные ещё не загружены из 1С
  const pollRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    const isDataEmpty = items.length === 0 && !error;
    if (isDataEmpty) {
      pollRef.current = setInterval(() => {
        api.get('/procurement/items').then(r => {
          const data = r.data;
          if (Array.isArray(data) && data.length > 0) {
            clearInterval(pollRef.current);
            loadAll();
          } else if (data && !data.loading && Array.isArray(data.items) && data.items.length > 0) {
            clearInterval(pollRef.current);
            loadAll();
          }
        }).catch(() => {});
      }, 8000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [loading, items.length, error, loadAll]);

  const [sendingAlert, setSendingAlert] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/procurement/refresh');
      await loadAll();
      showToast('Данные обновлены');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSendAlert = async () => {
    setSendingAlert(true);
    try {
      await api.post('/procurement/send-alert');
      showToast('✅ Алерт отправлен закупщикам в Telegram');
    } catch (e) {
      showToast('❌ Ошибка отправки алерта');
    } finally {
      setSendingAlert(false);
    }
  };

  const handleLoadHistory = async () => {
    setHistLoading(true);
    try {
      const r = await api.post('/procurement/load-history');
      showToast(r.data.message);
      // Обновляем статус через 3 сек
      setTimeout(() => api.get('/procurement/status').then(r => setStatus(r.data)), 3000);
    } finally {
      setHistLoading(false);
    }
  };

  const handleCreateOrder = async (body, opts = {}) => {
    setCreatingOrder(true);
    try {
      const r = await api.post('/procurement/orders/create', body);
      if (!r.data?.ok) {
        showToast(r.data?.message || 'Нет позиций для документа');
        return r.data;
      }
      showToast(`Создано ${r.data.created} документ(ов) закупки`);
      const ordersR = await api.get('/procurement/orders');
      setOrders(ordersR.data || []);
      if (!opts.stay) setTab('orders');
      return r.data;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Ошибка создания документа';
      showToast(`Ошибка: ${String(msg).slice(0, 160)}`);
      return null;
    } finally {
      setCreatingOrder(false);
    }
  };

  const handleCreateReceipt = async (body) => {
    const r = await api.post('/procurement/receipts', body);
    showToast(`Приёмка #${r.data.receipt_id} создана. Расхождений: ${r.data.discrepancies_count}`);
    const receiptsR = await api.get('/procurement/receipts');
    setReceipts(receiptsR.data || []);
  };

  const handleSaveActuals = async (body) => {
    const r = await api.post('/procurement/control', body);
    setControl(r.data);
    showToast(`Контроль рассчитан: попадание ${r.data.hit_rate_pct}%`);
    return r.data;
  };

  const TABS = [
    { id: 'kpi',      label: '⚡ KPI' },
    { id: 'top20',    label: '🏆 ТОП-20' },
    { id: 'trends',   label: '📈 Тренды роста' },
    { id: 'analytics',label: '🧠 ABC/XYZ' },
    { id: 'dk',       label: '🗝️ DK ассортимент' },
    { id: 'items',    label: '📦 Список закупки' },
    { id: 'cats',     label: '📂 Категории' },
    { id: 'site',     label: '🌐 Сверка с сайтом' },
    { id: 'orders',   label: '📋 Документы' },
    { id: 'receipts', label: '✅ Приёмка' },
    { id: 'control',  label: '🎯 Контроль' },
    { id: 'prices',   label: '💰 Цены' },
  ];

  return (
    <div style={{ position: 'relative' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 32, right: 32, zIndex: 1000,
          background: '#0e9f6e', color: '#fff', padding: '12px 20px',
          borderRadius: 10, fontWeight: 700, fontSize: 14, boxShadow: 'none',
          animation: 'fadeIn 0.3s',
        }}>{toast}</div>
      )}
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }`}</style>

      {/* Заголовок */}
      <div className="kp-hero" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Plate>1С · закупки</Plate>
          <h1 style={{ margin: 0 }}>Закупщик Pro</h1>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 8 }}>
            AI-коммерческий директор по товарке • данные из 1С
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleSendAlert} disabled={sendingAlert}
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none',
              background: sendingAlert ? 'var(--bg3)' : 'var(--accent)',
              color: '#fff', cursor: sendingAlert ? 'not-allowed' : 'pointer',
              fontWeight: 700, fontSize: 13,
              boxShadow: sendingAlert ? 'none' : 'none' }}>
            {sendingAlert ? '📤 Отправка...' : '📲 Отправить закупщикам'}
          </button>
          <button onClick={handleRefresh} disabled={refreshing}
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none',
              background: refreshing ? 'var(--bg3)' : 'var(--accent)',
              color: '#fff', cursor: refreshing ? 'not-allowed' : 'pointer',
              fontWeight: 700, fontSize: 13,
              boxShadow: refreshing ? 'none' : 'none' }}>
            {refreshing ? '⏳ Обновление...' : '🔄 Обновить данные'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: '#e0484822', border: '1px solid #e04848',
          borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: '#e04848', fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}

      {/* Статус загрузки данных */}
      {status && (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '12px 18px', marginBottom: 16,
          display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', flex: 1 }}>
            <span style={{ color: status.stock_ready ? '#0e9f6e' : '#e08a00' }}>
              {status.stock_ready ? '✓' : '⏳'} Склад: {status.stock_count} SKU
            </span>
            <span style={{ color: status.prices_ready ? '#0e9f6e' : '#e08a00' }}>
              {status.prices_ready ? '✓' : '⏳'} Цены: {status.prices_count} SKU
            </span>
            <span style={{ color: status.history_ready ? '#0e9f6e' : status.history_loading ? '#e08a00' : 'var(--text2)' }}>
              {status.history_ready ? '✓' : status.history_loading ? '⏳' : '○'}
              {' '}История 24 мес: {status.history_ready ? `${status.history_skus} SKU` : status.history_loading ? 'загружается...' : 'не загружена'}
              {status.history_age_h != null && ` (${status.history_age_h}ч назад)`}
            </span>
          </div>
          {!status.history_ready && !status.history_loading && (
            <button onClick={handleLoadHistory} disabled={histLoading}
              style={{ padding: '6px 14px', borderRadius: 7, border: 'none',
                background: 'var(--yellow)',
                color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                whiteSpace: 'nowrap' }}>
              {histLoading ? '⏳ Запуск...' : '📅 Загрузить историю 2 года'}
            </button>
          )}
          {status.history_loading && (
            <span style={{ color: '#e08a00', fontSize: 12, fontWeight: 600 }}>
              ⏳ Загрузка истории (~10-15 мин)...
            </span>
          )}
        </div>
      )}

      {/* KPI шапка — всегда видна */}
      {kpi && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, minmax(0, 1fr))',
          gap: 12, marginBottom: 20 }}>
          <KpiCard label="Критично (0 остаток)" value={kpi.critical_count}
            color={kpi.critical_count > 0 ? '#e04848' : '#0e9f6e'} icon="🔴" />
          <KpiCard label="Срочно к закупке"    value={kpi.urgent_count}
            color={kpi.urgent_count > 0 ? '#e08a00' : '#0e9f6e'} icon="🟡" />
          <KpiCard label="Скоро закончится"    value={kpi.soon_count}
            color={kpi.soon_count > 0 ? '#e08a00' : '#0e9f6e'} icon="🟠" />
          <KpiCard label="Растущий спрос"      value={kpi.growing_count} icon="📈" color="#0e9f6e" />
          <KpiCard label="К закупке (сумма)"   value={fmtMoney(kpi.total_rec_amount)} color="#0e9f6e" icon="🛒" />
          <KpiCard label="Потери / нед."       value={fmtMoney(kpi.money_loss_week)}
            color={kpi.money_loss_week > 0 ? '#e04848' : '#0e9f6e'}
            sub="от нулевых остатков" icon="💸" />
          <KpiCard label="Позиций к закупке"   value={kpi.skus_to_buy} color="#00a7dc" />
          <KpiCard label="Всего SKU"           value={kpi.total_skus} color="var(--text2)" />
        </div>
      )}

      {/* Вкладки */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap',
        borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '8px 14px', borderRadius: '8px 8px 0 0', border: 'none',
              background: tab === t.id ? 'var(--accent)' : 'transparent',
              color: tab === t.id ? '#fff' : 'var(--text2)',
              cursor: 'pointer', fontWeight: tab === t.id ? 700 : 400, fontSize: 13,
              borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              transition: 'all 0.15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Контент вкладок */}
      <Card>
        <div style={{ padding: 20 }}>
          {tab === 'kpi' && (
            <div>
              <SectionHeader title="ТОП-10 приоритетных позиций" />
              {loading ? <Spinner /> : items.length === 0 ? (
                <div style={{ padding: '30px 20px', textAlign: 'center' }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text1)', marginBottom: 8 }}>
                    Прогреваем кэш 1С...
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', maxWidth: 400, margin: '0 auto' }}>
                    Данные из 1С загружаются в фоне. Это занимает <b style={{color:'#e08a00'}}>1–2 минуты</b> при первом открытии.
                    Страница обновится автоматически.
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <div style={{ width: 200, height: 4, background: 'var(--border)',
                      borderRadius: 2, margin: '0 auto', overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: 'var(--accent)',
                        borderRadius: 2, animation: 'progress 2s ease-in-out infinite',
                        width: '40%' }} />
                    </div>
                  </div>
                  <style>{`@keyframes progress {
                    0%   { transform: translateX(-100%); width: 40%; }
                    50%  { width: 60%; }
                    100% { transform: translateX(350%); width: 40%; }
                  }`}</style>
                </div>
              ) : <Top20Block items={top20.slice(0, 10)} />}
            </div>
          )}

          {tab === 'top20' && (
            <div>
              <SectionHeader title="ТОП-20 к закупке сегодня" />
              {loading ? <Spinner /> : <Top20Block items={top20} />}
            </div>
          )}

          {tab === 'trends' && (
            <div>
              <SectionHeader title="📈 Растущий спрос — зона риска" />
              <GrowingTrendsBlock items={items} loading={loading} />
            </div>
          )}

          {tab === 'analytics' && (
            <div>
              <SectionHeader title="ABC/XYZ — структура ассортимента" />
              <ActionableAnalyticsBlock
                analytics={analytics}
                loading={loading}
                items={items}
                onCreateOrder={handleCreateOrder}
                creating={creatingOrder}
              />
            </div>
          )}

          {tab === 'dk' && (
            <div>
              <SectionHeader title="DK — анализ ассортимента и закупки" />
              <DkBlock items={items} analytics={analytics} loading={loading} />
            </div>
          )}

          {tab === 'items' && (
            <div>
              <SectionHeader title="Полный список закупки"
                action={
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: 'var(--text2)' }}>
                      {items.filter(i => i.recommended_qty > 0).length} позиций к закупке
                    </span>
                    <button
                      onClick={() => { setTab('orders'); }}
                      style={{ padding: '6px 14px', borderRadius: 7, border: 'none',
                        background: 'var(--accent)',
                        color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
                      📋 В документ →
                    </button>
                  </div>
                }
              />
              <ItemsTable items={items} loading={loading} focusSku={focusSku} />
            </div>
          )}

          {tab === 'cats' && (
            <div>
              <SectionHeader title="По категориям" />
              {loading ? <Spinner /> : <CategoriesBlock data={categories} />}
            </div>
          )}

          {tab === 'site' && (
            <div>
              <SectionHeader title="🌐 Сверка каталога сайта с 1С" />
              <SiteReconcileBlock
                showToast={showToast}
                onOrderCreated={async () => {
                  const ordersR = await api.get('/procurement/orders').catch(() => ({ data: [] }));
                  setOrders(ordersR.data || []);
                  setTab('orders');
                }}
              />
            </div>
          )}

          {tab === 'orders' && (
            <div>
              <SectionHeader title="Документы закупки" />
              <OrdersBlock orders={orders} onCreateOrder={handleCreateOrder} creating={creatingOrder} />
            </div>
          )}

          {tab === 'receipts' && (
            <div>
              <SectionHeader title="Приёмка товара" />
              <ReceiptBlock receipts={receipts} onCreateReceipt={handleCreateReceipt} />
            </div>
          )}

          {tab === 'control' && (
            <div>
              <SectionHeader title="Контроль закупщика" />
              <ControlBlock control={control} onSaveActuals={handleSaveActuals} orders={orders} />
            </div>
          )}

          {tab === 'prices' && (
            <div>
              <SectionHeader title="Ценообразование — рекомендации" />
              <PricesBlock items={items} loading={loading} />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// ─── Растущие тренды — зона риска ────────────────────────────────────────────
function GrowingTrendsBlock({ items, loading }) {
  const [filterCat, setFilterCat]         = useState('all');
  const [filterUrgency, setFilterUrgency] = useState('all');
  const [search, setSearch]               = useState('');
  const [sortKey, setSortKey]             = useState('days_left');
  const [sortAsc, setSortAsc]             = useState(true);

  const growing    = items.filter(i => i.trend === 'growing');
  const categories = [...new Set(growing.map(i => i.category))].sort();

  const getRowRisk = (item) => {
    if (item.days_left <= 7)  return 'critical';
    if (item.days_left <= 14) return 'urgent';
    if (item.days_left <= 21) return 'soon';
    return 'ok';
  };

  const filtered = growing
    .filter(i => filterCat === 'all' || i.category === filterCat)
    .filter(i => filterUrgency === 'all' || getRowRisk(i) === filterUrgency)
    .filter(i => !search
      || i.sku.toLowerCase().includes(search.toLowerCase())
      || (i.name || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let va = a[sortKey] ?? 0, vb = b[sortKey] ?? 0;
      if (sortKey === 'days_left') {
        va = va > 9000 ? 99999 : va;
        vb = vb > 9000 ? 99999 : vb;
      }
      return sortAsc ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });

  const critical = growing.filter(i => getRowRisk(i) === 'critical');
  const urgent   = growing.filter(i => getRowRisk(i) === 'urgent');
  const totalAmt = growing.reduce((s, i) => s + (i.recommended_qty || 0) * (i.purchase_price || 0), 0);
  const validDays = growing.filter(i => i.days_left < 9000);
  const avgDays  = validDays.length
    ? validDays.reduce((s, i) => s + i.days_left, 0) / validDays.length
    : 0;
  const riskPct  = growing.length
    ? Math.round((critical.length + urgent.length) / growing.length * 100)
    : 0;

  const Th = ({ k, label, right }) => (
    <th
      onClick={() => { setSortAsc(sortKey === k ? !sortAsc : k === 'days_left'); setSortKey(k); }}
      style={{
        padding: '8px 12px', textAlign: right ? 'right' : 'left',
        color: sortKey === k ? 'var(--accent)' : 'var(--text2)',
        fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none',
      }}
    >
      {label} {sortKey === k ? (sortAsc ? '↑' : '↓') : ''}
    </th>
  );

  if (loading) return <Spinner />;

  return (
    <div>
      {/* Алерт-баннер высокого риска */}
      {riskPct >= 30 && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(224,72,72,0.15), rgba(224,138,0,0.1))',
          border: '1px solid rgba(224,72,72,0.4)', borderRadius: 12,
          padding: '14px 18px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 28 }}>🚨</span>
          <div>
            <div style={{ fontWeight: 800, color: '#e04848', fontSize: 14 }}>
              Высокий риск дефицита! {riskPct}% растущих позиций — критично или срочно.
            </div>
            <div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 3 }}>
              Требуется немедленная закупка {critical.length + urgent.length} позиций на сумму{' '}
              <b style={{ color: '#e04848' }}>
                {fmtMoney([...critical, ...urgent].reduce(
                  (s, i) => s + (i.recommended_qty || 0) * (i.purchase_price || 0), 0))}
              </b>
            </div>
          </div>
        </div>
      )}

      {/* KPI-карточки */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: 12, marginBottom: 20,
      }}>
        <div style={{ background: 'var(--bg3)', border: '1px solid rgba(14,159,110,0.4)', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Растущих позиций</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#0e9f6e' }}>📈 {growing.length}</div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>пользуются спросом</div>
        </div>
        <div style={{ background: 'var(--bg3)', border: '1px solid rgba(224,72,72,0.5)', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Критично (≤7 дней)</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#e04848' }}>🔴 {critical.length}</div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>закончится на этой неделе</div>
        </div>
        <div style={{ background: 'var(--bg3)', border: '1px solid rgba(224,138,0,0.5)', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Срочно (≤14 дней)</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#e08a00' }}>🟡 {urgent.length}</div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>закончится за 2 недели</div>
        </div>
        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Ср. запас дней</div>
          <div style={{ fontSize: 26, fontWeight: 800,
            color: avgDays <= 14 ? '#e04848' : avgDays <= 30 ? '#e08a00' : '#0e9f6e' }}>
            {avgDays.toFixed(0)} дн
          </div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>по растущим позициям</div>
        </div>
        <div style={{ background: 'var(--bg3)', border: '1px solid rgba(14,159,110,0.4)', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>К закупке (сумма)</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0e9f6e' }}>{fmtMoney(totalAmt)}</div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>по всем растущим</div>
        </div>
        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Уровень риска</div>
          <div style={{ height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden', marginBottom: 6 }}>
            <div style={{
              height: '100%', borderRadius: 4, transition: 'width 0.6s',
              width: `${Math.min(riskPct, 100)}%`,
              background: riskPct >= 50 ? '#e04848' : riskPct >= 30 ? '#e08a00' : '#0e9f6e',
            }} />
          </div>
          <div style={{ fontSize: 20, fontWeight: 800,
            color: riskPct >= 50 ? '#e04848' : riskPct >= 30 ? '#e08a00' : '#0e9f6e' }}>
            {riskPct}%
          </div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>критично + срочно</div>
        </div>
      </div>

      {/* Мини-карты по категориям */}
      {categories.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text1)', marginBottom: 10 }}>
            🗂️ По категориям — кликните для фильтра
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {categories.map(cat => {
              const catItems = growing.filter(i => i.category === cat);
              const catCrit  = catItems.filter(i => getRowRisk(i) === 'critical').length;
              const catUrg   = catItems.filter(i => getRowRisk(i) === 'urgent').length;
              const catAmt   = catItems.reduce((s, i) => s + (i.recommended_qty || 0) * (i.purchase_price || 0), 0);
              const hasFire  = catCrit > 0;
              const active   = filterCat === cat;
              return (
                <div
                  key={cat}
                  onClick={() => setFilterCat(active ? 'all' : cat)}
                  style={{
                    cursor: 'pointer', minWidth: 110,
                    background: active ? 'rgba(0,167,220,0.15)' : 'var(--bg3)',
                    border: `1px solid ${active ? 'var(--accent)' : hasFire ? 'rgba(224,72,72,0.5)' : catUrg > 0 ? 'rgba(224,138,0,0.4)' : 'var(--border)'}`,
                    borderRadius: 10, padding: '10px 14px',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ fontSize: 16, fontWeight: 800, color: active ? 'var(--accent)' : 'var(--text1)' }}>
                    {hasFire ? '🔥 ' : catUrg > 0 ? '⚠️ ' : '📈 '}{cat}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>
                    {catItems.length} растущих
                  </div>
                  {catCrit > 0 && (
                    <div style={{ fontSize: 11, color: '#e04848', fontWeight: 700 }}>🚨 {catCrit} критично</div>
                  )}
                  {catUrg > 0 && (
                    <div style={{ fontSize: 11, color: '#e08a00', fontWeight: 600 }}>{catUrg} срочно</div>
                  )}
                  <div style={{ fontSize: 11, color: '#0e9f6e', marginTop: 2 }}>{fmtMoney(catAmt)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Фильтры */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Поиск по SKU / названию..."
          style={{ flex: 1, minWidth: 200, padding: '8px 12px', background: 'var(--bg3)',
            border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text1)', fontSize: 13 }}
        />
        <select value={filterUrgency} onChange={e => setFilterUrgency(e.target.value)}
          style={{ padding: '8px 12px', background: 'var(--bg3)', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--text1)', fontSize: 13 }}>
          <option value="all">Все риски</option>
          <option value="critical">🔴 Критично (≤7д)</option>
          <option value="urgent">🟡 Срочно (≤14д)</option>
          <option value="soon">🟠 Скоро (≤21д)</option>
          <option value="ok">🟢 Запас ОК</option>
        </select>
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
          style={{ padding: '8px 12px', background: 'var(--bg3)', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--text1)', fontSize: 13 }}>
          <option value="all">Все категории</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
          {filtered.length} / {growing.length} позиций
        </span>
      </div>

      {/* Таблица */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,36,37,0.05)' }}>
              <Th k="sku"             label="Артикул" />
              <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text2)', fontSize: 12 }}>Наименование</th>
              <Th k="category"        label="Кат." />
              <Th k="days_left"       label="Запас (дней)" />
              <Th k="stock"           label="Остаток (шт)" />
              <Th k="avg_sales"       label="Прод./день" />
              <Th k="sales_30d"       label="Прод. 30д" />
              <Th k="recommended_qty" label="К закупке" />
              <Th k="purchase_price"  label="Сумма" right />
            </tr>
          </thead>
          <tbody>
            {filtered.map((item, idx) => {
              const risk     = getRowRisk(item);
              const rowBg    = risk === 'critical' ? 'rgba(224,72,72,0.08)'
                             : risk === 'urgent'   ? 'rgba(224,138,0,0.05)'
                             : risk === 'soon'     ? 'rgba(224,138,0,0.04)' : 'transparent';
              const daysStr  = item.days_left >= 9999 ? '∞' : Number(item.days_left).toFixed(1);
              const daysClr  = risk === 'critical' ? '#e04848'
                             : risk === 'urgent'   ? '#e08a00'
                             : risk === 'soon'     ? '#e08a00' : '#0e9f6e';
              const riskIcon = risk === 'critical' ? '🚨' : risk === 'urgent' ? '⚠️' : risk === 'soon' ? '⏰' : '';
              return (
                <tr key={item.sku + idx} style={{ borderBottom: '1px solid var(--border)', background: rowBg }}>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{
                      background: 'rgba(0,167,220,0.15)', border: '1px solid rgba(0,167,220,0.4)',
                      color: '#00a7dc', fontWeight: 800, fontSize: 11,
                      padding: '2px 8px', borderRadius: 4, letterSpacing: '0.05em', fontFamily: 'monospace',
                    }}>{item.sku}</span>
                  </td>
                  <td style={{ padding: '8px 12px', maxWidth: 260,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>
                    {risk === 'critical' && <span style={{ marginRight: 4 }}>🔥</span>}
                    {item.name}
                  </td>
                  <td style={{ padding: '8px 12px', fontWeight: 700, color: 'var(--accent)' }}>{item.category}</td>
                  <td style={{ padding: '8px 12px', fontWeight: 800, color: daysClr, whiteSpace: 'nowrap' }}>
                    {riskIcon && <span style={{ marginRight: 3 }}>{riskIcon}</span>}
                    {daysStr} дн
                  </td>
                  <td style={{ padding: '8px 12px', color: 'var(--text2)' }}>{fmtNum(item.stock)} шт</td>
                  <td style={{ padding: '8px 12px', color: '#0e9f6e', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    📈 {Number(item.avg_sales || 0).toFixed(2)}/д
                  </td>
                  <td style={{ padding: '8px 12px', color: 'var(--text2)' }}>{fmtNum(item.sales_30d)} шт</td>
                  <td style={{ padding: '8px 12px', fontWeight: 800,
                    color: item.recommended_qty > 0 ? 'var(--text1)' : 'var(--text2)', whiteSpace: 'nowrap' }}>
                    {item.recommended_qty > 0 ? `${item.recommended_qty} шт` : '—'}
                  </td>
                  <td style={{ padding: '8px 12px', color: '#0e9f6e', fontWeight: 700,
                    whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {fmtMoney((item.recommended_qty || 0) * (item.purchase_price || 0))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!filtered.length && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>
            {growing.length === 0
              ? '📊 Нет позиций с растущим трендом — все в стабильной зоне'
              : '🔍 Ничего не найдено по заданным фильтрам'}
          </div>
        )}
      </div>

      {/* Итоговая строка */}
      {filtered.length > 0 && (
        <div style={{
          borderTop: '1px solid var(--border)', padding: '12px 12px 0',
          display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 4,
        }}>
          <span style={{ fontSize: 13, color: 'var(--text2)' }}>
            Итого к закупке (выборка):
            <b style={{ color: '#0e9f6e', marginLeft: 6 }}>
              {fmtMoney(filtered.reduce((s, i) => s + (i.recommended_qty || 0) * (i.purchase_price || 0), 0))}
            </b>
          </span>
          <span style={{ fontSize: 13, color: 'var(--text2)' }}>
            Кол-во:
            <b style={{ color: 'var(--text1)', marginLeft: 6 }}>
              {fmtNum(filtered.reduce((s, i) => s + (Number(i.recommended_qty) || 0), 0))} шт
            </b>
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Ценообразование ──────────────────────────────────────────────────────────
function PricesBlock({ items, loading }) {
  const [filterTrend, setFilterTrend] = useState('all');
  const priced = items.filter(i => i.purchase_price > 0);
  const filtered = filterTrend === 'all' ? priced : priced.filter(i => i.trend === filterTrend);

  const getChgColor = (cur, sug) => {
    if (!cur || !sug) return 'var(--text2)';
    const diff = sug - cur;
    if (diff > 0) return '#0e9f6e';
    if (diff < 0) return '#e04848';
    return 'var(--text2)';
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <select value={filterTrend} onChange={e => setFilterTrend(e.target.value)}
          style={{ padding: '8px 12px', background: 'var(--bg3)', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--text1)', fontSize: 13 }}>
          <option value="all">Все тренды</option>
          <option value="growing">📈 Растущий</option>
          <option value="falling">📉 Падающий</option>
          <option value="stable">➡️ Стабильный</option>
        </select>
        <div style={{ display: 'flex', alignItems: 'center', color: 'var(--text2)', fontSize: 12 }}>
          {filtered.length} позиций
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,36,37,0.05)' }}>
                {['SKU', 'Наименование', 'Категория', 'Тренд', 'Срочность',
                  'Закупка', 'Текущая цена', 'Рек. цена', 'Δ цена', 'Маржа %', 'Обоснование'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left',
                    color: 'var(--text2)', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, i) => {
                const delta = item.suggested_price && item.sale_price
                  ? item.suggested_price - item.sale_price : 0;
                const marginPct = item.purchase_price
                  ? ((item.sale_price - item.purchase_price) / item.purchase_price * 100).toFixed(1)
                  : 0;
                return (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text2)' }}>{item.sku}</td>
                    <td style={{ padding: '8px 12px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${item.sku||''} ${item.name}`}>
                      {item.sku && <span style={{
                        display: 'inline-block', marginRight: 6,
                        background: 'rgba(0,167,220,0.15)', border: '1px solid rgba(0,167,220,0.4)',
                        color: '#00a7dc', fontWeight: 800, fontSize: 10,
                        padding: '1px 6px', borderRadius: 4, letterSpacing: '0.05em',
                        whiteSpace: 'nowrap', verticalAlign: 'middle',
                      }}>{item.sku}</span>}
                      <span style={{ verticalAlign: 'middle' }}>{item.name}</span>
                    </td>
                    <td style={{ padding: '8px 12px', fontWeight: 700, color: 'var(--accent)' }}>{item.category}</td>
                    <td style={{ padding: '8px 12px', color: TREND_COLOR[item.trend] }}>
                      {TREND_ICON[item.trend]} {item.trend}
                    </td>
                    <td style={{ padding: '8px 12px' }}><Badge type={item.urgency} /></td>
                    <td style={{ padding: '8px 12px', color: 'var(--text2)' }}>{fmtMoney(item.purchase_price)}</td>
                    <td style={{ padding: '8px 12px' }}>{fmtMoney(item.sale_price)}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 700 }}>{fmtMoney(item.suggested_price)}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 700,
                      color: getChgColor(item.sale_price, item.suggested_price) }}>
                      {delta !== 0 ? `${delta > 0 ? '+' : ''}${fmtMoney(delta)}` : '—'}
                    </td>
                    <td style={{ padding: '8px 12px', color: marginPct > 30 ? '#0e9f6e' : '#e08a00' }}>
                      {marginPct}%
                    </td>
                    <td style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text2)',
                      maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={item.price_note}>
                      {item.price_note || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!filtered.length && <div style={{ padding: 30, textAlign: 'center', color: 'var(--text2)' }}>Нет данных</div>}
        </div>
      )}
    </div>
  );
}
